import { Reducer } from "../../../lib/redux";
import { extend } from "../../../utils/vanilla";
import {
    CoachingActions,
    IEnqueueCoachingCompletionPayload,
    IEnqueueNewSessionResolutionPayload,
    IReassignPendingCommentsPayload,
    IRemovePendingSessionBucketPayload,
    ISetNewSessionResolutionPayload,
    IUpdateCoachingCompletionStatePayload
} from "./actions";
import {
    CoachingActionTypes,
    ICoachingComment,
    ICoachingStore
} from "./types";

export const defaultCoaching: IIdDictionary<ICoachingStore> = {};

type TCoachingState = IIdDictionary<ICoachingStore>;

const applySetCoachingComments = (
    state: TCoachingState,
    { userId, sessionId, comments }: { userId: string; sessionId: string; comments: ICoachingComment[] }
): TCoachingState => {
    if (!userId || !sessionId) {
        return state;
    }
    const existing = state[userId];
    // Keep unconfirmed local comments (syncStatus set) the server list lacks, so
    // a refetch can't wipe the offline queue; shared ids dedupe to the server copy.
    const serverComments = extend(true, [], comments) as ICoachingComment[];
    const serverIds = new Set(serverComments.map((c) => c.id));
    const unconfirmed = (existing?.comments?.[sessionId] ?? []).filter(
        (c) => c.syncStatus && !serverIds.has(c.id)
    );
    return {
        ...state,
        [userId]: {
            userId,
            sessions: existing?.sessions ?? [],
            comments: {
                ...(existing?.comments ?? {}),
                [sessionId]: [...serverComments, ...extend(true, [], unconfirmed)]
            },
            pendingCompletions: existing?.pendingCompletions ?? {},
            lastFetchTimestamp: existing?.lastFetchTimestamp ?? 0,
            newSessionResolution: existing?.newSessionResolution
        }
    };
};

const applyAddPendingComment = (
    state: TCoachingState,
    { userId, sessionId, comment }: { userId: string; sessionId: string; comment: ICoachingComment }
): TCoachingState => {
    if (!userId || !sessionId) {
        return state;
    }
    const existing = state[userId];
    const bucket = existing?.comments?.[sessionId] ?? [];
    return {
        ...state,
        [userId]: {
            userId,
            sessions: existing?.sessions ?? [],
            comments: {
                ...(existing?.comments ?? {}),
                [sessionId]: [...bucket, extend(true, {}, comment)]
            },
            pendingCompletions: existing?.pendingCompletions ?? {},
            lastFetchTimestamp: existing?.lastFetchTimestamp ?? 0,
            newSessionResolution: existing?.newSessionResolution
        }
    };
};

const applyEnqueueCompletion = (
    state: TCoachingState,
    { userId, sessionId, completion }: IEnqueueCoachingCompletionPayload
): TCoachingState => {
    if (!userId) {
        return state;
    }
    const existing = state[userId];
    return {
        ...state,
        [userId]: {
            userId,
            sessions: existing?.sessions ?? [],
            comments: existing?.comments ?? {},
            pendingCompletions: {
                ...(existing?.pendingCompletions ?? {}),
                [sessionId]: extend(true, {}, completion)
            },
            lastFetchTimestamp: existing?.lastFetchTimestamp ?? 0,
            newSessionResolution: existing?.newSessionResolution
        }
    };
};

const applyUpdateCompletionState = (
    state: TCoachingState,
    { userId, sessionId, patch }: IUpdateCoachingCompletionStatePayload
): TCoachingState => {
    const existing = state[userId];
    const record = existing?.pendingCompletions?.[sessionId];
    if (!record) {
        return state;
    }
    // A patch carrying a new sessionId re-keys the dictionary entry (NEW "" → resolved id);
    // otherwise the entry is patched in place under its current key.
    const targetKey = patch.sessionId ?? sessionId;
    const { [sessionId]: removed, ...rest } = existing.pendingCompletions;
    return {
        ...state,
        [userId]: {
            ...existing,
            pendingCompletions: {
                ...rest,
                [targetKey]: { ...record, ...patch }
            }
        }
    };
};

const applyReassignPendingComments = (
    state: TCoachingState,
    { userId, fromSessionId, toSessionId }: IReassignPendingCommentsPayload
): TCoachingState => {
    const existing = state[userId];
    const sourceComments = existing?.comments?.[fromSessionId];
    if (!existing || !sourceComments) {
        return state;
    }
    const targetComments = existing.comments[toSessionId];
    let merged: ICoachingComment[];
    if (targetComments) {
        const targetIds = new Set(targetComments.map((c) => c.id));
        const uniqueSource = sourceComments.filter((c) => !targetIds.has(c.id));
        merged = [...extend(true, [], targetComments), ...extend(true, [], uniqueSource)];
    } else {
        merged = extend(true, [], sourceComments);
    }
    const { [fromSessionId]: removed, ...restComments } = existing.comments;
    return {
        ...state,
        [userId]: {
            ...existing,
            comments: {
                ...restComments,
                [toSessionId]: merged
            }
        }
    };
};

/** Ensure a NEW-session resolution op exists for the user, without resetting an in-flight budget. */
const applyRemovePendingSessionComments = (
    state: TCoachingState,
    { userId, sessionId }: IRemovePendingSessionBucketPayload
): TCoachingState => {
    if (!userId || !sessionId) {
        return state;
    }
    const existing = state[userId];
    if (!existing) {
        return state;
    }
    const { [sessionId]: removed, ...restComments } = existing.comments;
    return {
        ...state,
        [userId]: { ...existing, comments: restComments }
    };
};

const applyRemovePendingSessionCompletion = (
    state: TCoachingState,
    { userId, sessionId }: IRemovePendingSessionBucketPayload
): TCoachingState => {
    if (!userId || !sessionId) {
        return state;
    }
    const existing = state[userId];
    if (!existing) {
        return state;
    }
    const { [sessionId]: removed, ...restCompletions } = existing.pendingCompletions;
    return {
        ...state,
        [userId]: { ...existing, pendingCompletions: restCompletions }
    };
};

const applyEnqueueNewSessionResolution = (
    state: TCoachingState,
    { userId }: IEnqueueNewSessionResolutionPayload
): TCoachingState => {
    if (!userId) {
        return state;
    }
    const existing = state[userId];
    return {
        ...state,
        [userId]: {
            userId,
            sessions: existing?.sessions ?? [],
            comments: existing?.comments ?? {},
            pendingCompletions: existing?.pendingCompletions ?? {},
            lastFetchTimestamp: existing?.lastFetchTimestamp ?? 0,
            newSessionResolution: existing?.newSessionResolution ?? {}
        }
    };
};

const applySetNewSessionResolution = (
    state: TCoachingState,
    { userId, resolution }: ISetNewSessionResolutionPayload
): TCoachingState => {
    const existing = state[userId];
    if (!existing) {
        return state;
    }
    return {
        ...state,
        [userId]: {
            ...existing,
            newSessionResolution: resolution
        }
    };
};

export const coaching: Reducer<IIdDictionary<ICoachingStore>, CoachingActions> = (
    state = defaultCoaching,
    action
) => {
    switch (action.type) {
        case CoachingActionTypes.SET_COACHING_SESSIONS: {
            const { userId, sessions } = action.payload;
            if (!userId) {
                return state;
            }
            const existing = state[userId];
            return {
                ...state,
                [userId]: {
                    userId,
                    sessions: extend(true, [], sessions),
                    comments: existing?.comments ?? {},
                    pendingCompletions: existing?.pendingCompletions ?? {},
                    lastFetchTimestamp: Date.now(),
                    newSessionResolution: existing?.newSessionResolution
                }
            };
        }
        case CoachingActionTypes.UPDATE_COACHING_SESSION: {
            const { userId, session } = action.payload;
            const existing = state[userId];
            if (!existing) {
                return state;
            }
            return {
                ...state,
                [userId]: {
                    ...existing,
                    sessions: existing.sessions.map((s) => (
                        s.id === session.id ? extend(true, {}, session) : s
                    ))
                }
            };
        }
        case CoachingActionTypes.SET_COACHING_COMMENTS:
            return applySetCoachingComments(state, action.payload);
        case CoachingActionTypes.ADD_PENDING_COACHING_COMMENT:
            return applyAddPendingComment(state, action.payload);
        case CoachingActionTypes.UPDATE_PENDING_COMMENT_STATE: {
            const { userId, sessionId, commentId, patch } = action.payload;
            const existing = state[userId];
            const bucket = existing?.comments?.[sessionId];
            if (!bucket) {
                return state;
            }
            return {
                ...state,
                [userId]: {
                    ...existing,
                    comments: {
                        ...existing.comments,
                        [sessionId]: bucket.map((c) =>
                            c.id === commentId ? { ...c, ...patch } : c)
                    }
                }
            };
        }
        case CoachingActionTypes.ENQUEUE_COACHING_COMPLETION:
            return applyEnqueueCompletion(state, action.payload);
        case CoachingActionTypes.UPDATE_COACHING_COMPLETION_STATE:
            return applyUpdateCompletionState(state, action.payload);
        case CoachingActionTypes.REASSIGN_PENDING_COMMENTS:
            return applyReassignPendingComments(state, action.payload);
        case CoachingActionTypes.ENQUEUE_NEW_SESSION_RESOLUTION:
            return applyEnqueueNewSessionResolution(state, action.payload);
        case CoachingActionTypes.SET_NEW_SESSION_RESOLUTION:
            return applySetNewSessionResolution(state, action.payload);
        case CoachingActionTypes.REMOVE_PENDING_SESSION_COMMENTS:
            return applyRemovePendingSessionComments(state, action.payload);
        case CoachingActionTypes.REMOVE_PENDING_SESSION_COMPLETION:
            return applyRemovePendingSessionCompletion(state, action.payload);
        case CoachingActionTypes.CLEAR_COACHING_STATE: {
            if (action.payload) {
                const newState = { ...state };
                delete newState[action.payload];
                return newState;
            }
            return defaultCoaching;
        }
        default:
            return state;
    }
};
